
import React, { useEffect, useMemo, useState, useRef } from "react";
import Chart from 'chart.js/auto';
import { fetchDealerNetworkStatus } from "../../../actions/dashboardAction";
import '../index.css';
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import { withTheme } from "../../../themes/theming";
import { AUTO_DEFAULT_FILTER } from "../../../AppConstant";

const PieChart = (props) => {
    const { dispatch, dealerOperations } = props;
    const chartRef = useRef(null);
    const chartInstanceRef = useRef(null);
    const [data, setData] = useState([]);
    const [hasFetched, setHasFetched] = useState(false);
    var I18n = require("react-redux-i18n").I18n;

    /** Handler for API success callback */
    const onSuccess = (response) => {
        // Transform the response object into array of label-value pairs for pie chart
        const transformedData = [
            { label: I18n.t('dashboardElements.NotStartedPercentage'), value: response.notStartedPercentage || 0 },
            { label: I18n.t('dashboardElements.OngoingPercentage'), value: response.ongoingPercentage || 0 },
            { label: I18n.t('dashboardElements.PassedPercentage'), value: response.passedPercentage || 0 },
            { label: I18n.t('dashboardElements.FailedPercentage'), value: response.failedPercentage || 0 },
            { label: I18n.t('dashboardElements.FailedKaizenOngoingPercentage'), value: response.failedKaizenOngoingPercentage || 0 },
        ];
        // Show all segments including 0 values
        const filteredData = transformedData;
        setData(filteredData);
    };
    /** Handler for API failure callback */
    const onFailure = (error) => console.error("❌ Error while fetching list of video", error);

    const fetchProgressBarData = () => {
        try {
            if (!props.toolboxByType?.toolboxByType || props.toolboxByType.toolboxByType?.length === 0) {
                return;
            }

            const toolBoxList = props.toolboxByType?.toolboxByType;
            const cycleList = props.cycleDataList;

            if (!toolBoxList || toolBoxList.length === 0 || !cycleList || cycleList.length === 0) {
                console.warn("Required data is not ready yet.");
                return;
            }

            const initialToolBoxId = toolBoxList[toolBoxList.length - 1];
            const initialCycle = cycleList[cycleList.length - 1];

            if (!initialCycle?.cycle) {
                console.warn("Initial cycle format is missing.");
                return;
            }
            const [initialStartDate, initialEndDate] = initialCycle.cycle.split(" to ");
            const nmscCode = props.existingProps.userMdl.nmsc;
            const activeProgram = props.existingProps.userMdl.activeProgram;

            const data = {
                "userNmscCode": props.existingProps.userMdl.nmsc,
                "userLangCode": props.existingProps.userMdl.lang,
                "programList": [props.existingProps.userMdl.activeProgram],
                "nmscCode": props.existingProps.nmscFlag ? [nmscCode.toUpperCase()] : props.existingProps.nmscFilter,
                "brand": props.existingProps.brandFilter,
                "country": props.existingProps.countryFilter,
                "region": props.existingProps.regionFilter,
                "userId": props.existingProps.userMdl.username,
                "businessArea": props.existingProps.userMdl.businessAreaKey,
                "toolboxIDList": (props.existingProps.toolboxIDList?.length > 0) ? props.existingProps.toolboxIDList : [Number(initialToolBoxId?.packageMapPrimaryKey)],
                "startDate": props.existingProps.startDate !== "" ? props.existingProps.startDate : initialStartDate,
                "endDate": props.existingProps.endDate !== "" ? props.existingProps.endDate : initialEndDate,
                "dealerOperations": dealerOperations?.length > 0 ? dealerOperations : (AUTO_DEFAULT_FILTER[activeProgram] || ['All'])
            }
            dispatch(fetchDealerNetworkStatus(onSuccess, onFailure, data))

        } catch (error) {
            console.error("Error fetching progress bar data:", error);
        }
    }


    useEffect(() => {
        const toolBoxReady = props.toolboxByType?.toolboxByType?.length > 0;
        const cycleReady = props.cycleDataList?.length > 0;

        if (toolBoxReady && cycleReady && !hasFetched) {
            fetchProgressBarData();
            setHasFetched(true);
        }
    }, [props.toolboxByType?.toolboxByType, props.cycleDataList, hasFetched]);


    useEffect(() => {
        if (!data || data.length === 0) return;

        const ctx = chartRef.current.getContext('2d');

        const labels = data.map(item => item.label);
        const values = data.map(item => item.value);

        const getBackgroundColors = (labels) => {
            return labels.map(label => {
                switch (label) {
                    case I18n.t('dashboardElements.NotStartedPercentage'): return '#1E5FA4'; // Blue
                    case I18n.t('dashboardElements.OngoingPercentage'): return '#D03C1A'; // Red
                    case I18n.t('dashboardElements.PassedPercentage'): return '#286111'; // Green
                    case I18n.t('dashboardElements.FailedPercentage'): return '#36A2EB'; // Light Blue
                    case I18n.t('dashboardElements.FailedKaizenOngoingPercentage'): return '#9326B1'; // Purple
                    default: return '#6c757d'; // Gray fallback
                }
            });
        };

        const percentagePlugin = {
            id: 'percentageText',
            afterDatasetsDraw: function (chart) {
                const ctx = chart.ctx;
                const meta = chart.getDatasetMeta(0);

                ctx.save();
                ctx.font = 'bold 12px Arial';
                ctx.fillStyle = 'white';
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';

                meta.data.forEach((arc, index) => {
                    const value = chart.data.datasets[0].data[index];
                    if (value > 0 && value > 10) {
                        const { x, y } = arc.tooltipPosition();
                        ctx.fillText(value + '%', x, y);
                    }
                });

                ctx.restore();
            }
        };

        chartInstanceRef.current = new Chart(ctx, {
            type: 'pie',
            data: {
                labels: labels,
                datasets: [
                    {
                        label: I18n.t('dashboardElements.DealerStatus'),
                        data: values,
                        backgroundColor: getBackgroundColors(labels),
                        borderWidth: 2,
                        borderColor: '#ffffff',
                        hoverOffset: 10 // Smooth hover effect
                    }
                ]
            },

            options: {
                responsive: true,
                maintainAspectRatio: false,
                interaction: {
                    mode: 'nearest',
                    intersect: true,
                    axis: 'xy'
                },
                hover: {
                    mode: 'nearest',
                    animationDuration: 0
                },
                animation: {
                    duration: 1000,
                    animateRotate: true,
                    animateScale: true
                },
                plugins: {
                    legend: {
                        position: 'bottom',
                        align: 'center',
                        labels: {
                            boxWidth: 15,
                            padding: 10,
                            font: {
                                size: 12
                            }
                        }
                    },
                    tooltip: {
                        enabled: true
                    },
                    percentageText: percentagePlugin
                },
                layout: {
                    padding: 10
                }
            },
            plugins: [percentagePlugin]
        });

        return () => {
            if (chartInstanceRef.current) {
                chartInstanceRef.current.destroy();
            }
        };
    }, [data]);


    return (
        <>
            <div className={`large-widget-header left-align-header widget-header-${props.theme?.name}`} title={I18n.t('dashboardHeaders.DealerNetworkStatusElement')} style={{ width: "100%" }}>
                {I18n.t('dashboardElements.DealerNetworkStatus')}
            </div>

            {/* Smaller chart container */}
            <div
                style={{
                    width: '100%',
                    maxWidth: '423px', // smaller width
                    height: '250px',   // smaller height
                    margin: '0 auto',
                    position: 'relative',
                }}
            >
                <canvas ref={chartRef}></canvas>
            </div>
        </>
    );
};

const mapStateToProps = state => {
    return {
        user: state.loginReducer,
        activityToolBox: state.activityReducer,
        cycleDataList: state.dashboardReducer.cycleDataList,
        toolboxByType: state.leftNavReducer
    };
};
export default withRouter(connect(mapStateToProps)(withTheme(PieChart)));
